import { Alert, Box, Paper, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MdxRenderer from '../blog/MdxRenderer';

/**
 * Local-only preview of a staged MDX post. `yarn blog:preview blog-src/<slug>`
 * compiles the post into `public/blog-preview/<slug>/`; this route fetches that
 * compiled module and renders it through the real registry with a same-origin
 * asset base, so it faithfully mirrors production without publishing anything.
 */
const BlogPreview: React.FC = () => {
  const { slug = '' } = useParams();
  const [code, setCode] = useState<string>();
  const [error, setError] = useState<string>();

  const base = `/blog-preview/${slug}`;

  useEffect(() => {
    let cancelled = false;
    setCode(undefined);
    setError(undefined);
    fetch(`${base}/index.js`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => !cancelled && setCode(text))
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
  }, [base]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Local preview of <code>{slug}</code> — not published. Re-run{' '}
        <code>yarn blog:preview blog-src/{slug}</code> after edits, then refresh.
      </Alert>
      <Paper elevation={6} sx={{ padding: '2em' }}>
        {error ? (
          <Typography color="error">
            Could not load preview ({error}). Did you run{' '}
            <code>yarn blog:preview blog-src/{slug}</code>?
          </Typography>
        ) : !code ? (
          <Skeleton variant="rectangular" sx={{ width: '100%', height: '20em' }} />
        ) : (
          <MdxRenderer code={code} slug={slug} assetBase="/blog-preview" />
        )}
      </Paper>
    </Box>
  );
};

export default BlogPreview;
